import { ComponentProps, PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import Score from '@material-ui/icons/Score';
import MenuBook from '@material-ui/icons/MenuBook';
import TimelineIcon from '@material-ui/icons/Timeline';
import SyncIcon from '@material-ui/icons/Sync';
import CloudIcon from '@material-ui/icons/Cloud';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import StorageIcon from '@material-ui/icons/Storage';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import CampaignIcon from '@material-ui/icons/Feedback';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { usePermission } from '@backstage/plugin-permission-react';
import type { BasicPermission } from '@backstage/plugin-permission-common';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { announcementCreatePermission } from '@backstage-community/plugin-announcements-common';
import { adminOrgAccountsReadPermission } from '@internal/plugin-admin-common';

type PermissionedSidebarItemProps = ComponentProps<typeof SidebarItem> & {
  permission: BasicPermission;
};

const PermissionedSidebarItem = ({
  permission,
  ...itemProps
}: PermissionedSidebarItemProps) => {
  const { loading, allowed } = usePermission({ permission });
  if (!loading && !allowed) return null;
  return <SidebarItem {...itemProps} />;
};

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        {/* <SidebarItem icon={BuildIcon} text="Services">
          <SidebarSubmenu title="Services">
          </SidebarSubmenu>
        </SidebarItem> */}
        <SidebarItem icon={HomeIcon} to="catalog" text="Catalog" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <PermissionedSidebarItem
          permission={catalogEntityCreatePermission}
          icon={CreateComponentIcon}
          to="create"
          text="Templates"
        />
        <PermissionedSidebarItem
          permission={announcementCreatePermission}
          icon={CampaignIcon}
          to="announcements"
          text="Announcements"
        />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          <PermissionedSidebarItem
            permission={catalogEntityCreatePermission}
            icon={Score}
            to="score-board"
            text="TemaBit Quality"
          />
          <PermissionedSidebarItem
            permission={catalogEntityCreatePermission}
            icon={MenuBook}
            to="backstage-adoption"
            text="Doc Adoption"
          />
          <SidebarItem icon={CloudIcon} to="aws-accounts" text="AWS Accounts" />
          <SidebarItem
            icon={TimelineIcon}
            to="tdp-skills-telemetry"
            text="AI Skills"
          />
          <SidebarItem
            icon={Score}
            to="tdp-statistic"
            text="Template Adoption"
          />
          <SidebarItem icon={SyncIcon} to="imc-sync" text="IMC Sync" />
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      {/*
        Admin button — pinned at the bottom, above Settings. The WHOLE item
        (button + its My-Groups-style hover flyout) is gated on
        adminOrgAccountsReadPermission via PermissionedSidebarItem, which spreads
        children through to SidebarItem — so a non-admin sees no Admin button at
        all. Its first (only, for now) entry is the Org-Accounts viewer.
      */}
      <PermissionedSidebarItem
        permission={adminOrgAccountsReadPermission}
        icon={SupervisorAccountIcon}
        text="Admin"
      >
        <SidebarSubmenu title="Admin">
          <SidebarSubmenuItem
            title="Org Accounts"
            to="admin/org-accounts"
            icon={StorageIcon}
          />
        </SidebarSubmenu>
      </PermissionedSidebarItem>
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
